import React, { useEffect, useState } from "react";
import { Title } from "../../../../../stories/Title/Title";
import Close from "../../../../../images/Close.svg";
import { Label } from "../../../../../stories/Label/Label";
import Input from "../../../../../stories/Input/Input";
import { Button } from "../../../../../stories/Button/Button";
import { DataTypes, Operation } from "../../../../../utils/Enum.types";
import { useLazyQuery, useMutation } from "@apollo/client";
import {
  GetProjectByNodeIdForIntData,
  GetProjectDailyReports,
  GetProjectsHeaders,
} from "../../masters/projects/queries/queries";
import {
  AtsAcLdgrProjects,
  AtsAddOrUpdateProjectInitDataResult,
  AtsAddOrUpdateProjectInitDataVar,
  AtsProjectDailyReportsOrderField,
  GetProjectDailyReportsResult,
  GetProjectDailyReportsVar,
  GetProjectsHeadersResult,
  GetProjectsHeadersVar,
} from "../../masters/projects/queries/types";
import useUrlParams from "../../../../../customHooks/useUrlParams";
import { nodeVar, OrderDirection } from "../../../../../utils/commonQueryTypes";
import InputNumber from "../../../../common/Input/InputNumber";
import { FormControlLabel, FormGroup } from "@mui/material";
import { AntSwitch } from "../../../../../styles/Switch";
import {
  AtsAddOrUpdateProjectInitData,
} from "../../masters/projects/queries/mutations";
import useLoggedInUserDetails from "../../../../../customHooks/useLoggedInUserDetails";
import {
  toInputStandardDate,
  toIsoDate,
} from "../../../../../utils/UtilFunctions";
import useServerDateandTime from "../../../../../customHooks/useServerDateandTime";
import { emptyMessage } from "../../../../../utils/constants";
import { msgType } from "../../../../../utils/types";
import MessageModal from "../../../../../customComponents/MessageModal";
import { Keys } from "../../../../../utils/Enum.keys";
interface Props {
  setModal: React.Dispatch<React.SetStateAction<boolean>>;
  operation: Operation;
}

const InitializeData = ({ setModal, operation }: Props) => {
  const { customer_id, projectID } = useUrlParams();
  const { serverDate } = useServerDateandTime();
  const [formData, setFormData] = useState<
    Record<string, string | number | boolean>
  >({});

  const [message, setMessage] = useState<msgType>(emptyMessage);

  const { user_details } = useLoggedInUserDetails();
  
  const [AddInitData, { loading }] = useMutation<
    AtsAddOrUpdateProjectInitDataResult,
    AtsAddOrUpdateProjectInitDataVar
  >(AtsAddOrUpdateProjectInitData, {
    onError: (e) => {
      setMessage({
        flag: true,
        message: e.message,
        operation: Operation.NONE,
      });
    },
  });

  const [GetProjectDetails, { data: projectData }] = useLazyQuery<
    { node: AtsAcLdgrProjects },
    nodeVar
  >(GetProjectByNodeIdForIntData);

  const [GetHeaders, { data: headerData }] = useLazyQuery<
    GetProjectsHeadersResult,
    GetProjectsHeadersVar
  >(GetProjectsHeaders);

  const [GetProjectsReports, { data: reportsData }] = useLazyQuery<
    GetProjectDailyReportsResult,
    GetProjectDailyReportsVar
  >(GetProjectDailyReports);

  useEffect(() => {
    if (customer_id && projectID) {
      GetHeaders({
        variables: {
          customerID: customer_id,
          projectID: projectID,
        },
      }).then(({ data }) => {
        if (data && data.GetProjectsHeaders) {
          const filteredData = data.GetProjectsHeaders.filter(
            (header) => header.initData
          );
          setFormData(
            filteredData.reduce((acc, curr) => {
              if (curr.dataType === DataTypes.BOOLEAN) {
                acc[curr.idx] = false;
              } else {
                acc[curr.idx] = "";
              }
              return acc;
            }, {} as Record<string, string | number | boolean>)
          );
        }
      });
    }
  }, [customer_id, projectID, GetHeaders]);

  useEffect(() => {
    if (projectID) {
      GetProjectDetails({
        variables: {
          id: projectID,
        },
      }).then(({ data }) => {
        if (data && data.node) {
          const filteredData = headerData
            ? headerData.GetProjectsHeaders.filter((header) => header.initData)
            : [];
          setFormData(
            filteredData.reduce((acc, curr) => {
              if (curr.dataType === DataTypes.DATE) {
                acc[curr.idx] = toInputStandardDate(
                  (data.node as any)[`Params${curr.idx}`]
                );
              } else {
                acc[curr.idx] = (data.node as any)[`Params${curr.idx}`];
              }
              return acc;
            }, {} as Record<string, string | number | boolean>)
          );
        }
      });
    }
  }, [
    customer_id,
    projectID,
    headerData,
    operation,
    projectData,
    GetProjectDetails,
    operation,
  ]);

  useEffect(() => {
    if (projectID && customer_id && operation === Operation.UPDATE) {
      GetProjectsReports({
        variables: {
          customerID: customer_id,
          projectID: projectID,
          startDate: toIsoDate(serverDate),
          endDate: toIsoDate(serverDate),
          after: null,
          first: 1,
          orderBy: {
            direction: OrderDirection.ASC,
            field: AtsProjectDailyReportsOrderField.REPORT_DATE,
          },
        },
      }).then(({ data }) => {
        if (data && data.GetProjectDailyReports.edges.length) {
          const filteredData = headerData
            ? headerData.GetProjectsHeaders.filter((header) => header.initData)
            : [];
          const node = data.GetProjectDailyReports.edges[0].node;

          setFormData(
            filteredData.reduce((acc, curr) => {
              if (curr.dataType === DataTypes.DATE) {
                acc[curr.idx] = toInputStandardDate(
                  (node as any)[`param${curr.idx}`]
                );
              } else {
                acc[curr.idx] = (node as any)[`param${curr.idx}`];
              }
              return acc;
            }, {} as Record<string, string | number | boolean>)
          );
        }
      });
    }
  }, [
    customer_id,
    projectID,
    headerData,
    operation,
    projectData,
    serverDate,
    GetProjectsReports,
    operation,
  ]);

  const handleValueChange = (
    e: React.ChangeEvent<HTMLTextAreaElement | HTMLInputElement>
  ) => {
    const { name, value } = e.target as HTMLInputElement;

    setFormData(() => ({
      ...formData,
      [name]: value,
    }));
  };

  const handleSubmit = () => {
    const datesExists = headerData
      ? headerData.GetProjectsHeaders.filter(
          (header) => header.dataType === DataTypes.DATE
        ).map((header) => header.idx)
      : [];
    let dateNotFilled: boolean = false;

    datesExists.forEach((date) => {
      if (formData[date] === "") {
        dateNotFilled = true;
      }
    });

    if (dateNotFilled) {
      setMessage({
        flag: true,
        message: "Please fill all the dates",
        operation: Operation.NONE,
      });
      return;
    }

    if (customer_id && projectID) {
      AddInitData({
        variables: {
          customerID: customer_id,
          projectID: projectID,
          reportDate: toIsoDate(serverDate),
          input: Object.entries(formData).map(([key, value]) => {
            const foundData = headerData
              ? headerData.GetProjectsHeaders.find(
                  (header) => header.idx === Number(key)
                )
              : undefined;
            if (foundData && foundData.dataType === DataTypes.DATE) {
              return {
                idx: Number(key),
                param: toIsoDate(value as string),
              };
            }
            return {
              idx: Number(key),
              param: value.toString(),
            };
          }),
          user_details,
        },
      }).then(({ data }) => {
        if (data && data.AtsAddOrUpdateProjectInitData) {
          setMessage({
            flag: true,
            message: `Initialize Data ${
              operation === Operation.CREATE ? "updated" : "added"
            } successfully`,
            operation: Operation.CREATE,
          });
        }
      });
    }
  };

  const handleClear = () => {
    const filteredData = headerData
      ? headerData.GetProjectsHeaders.filter((header) => header.initData)
      : [];

    const clearedData = filteredData.reduce((acc, curr) => {
      if (curr.dataType === DataTypes.BOOLEAN) {
        acc[curr.idx] = false;
      } else {
        acc[curr.idx] = "";
      }
      return acc;
    }, {} as Record<string, string | number | boolean>);

    setFormData(clearedData);
  };

  const handleClose = () => {
    if (message.operation !== Operation.NONE) {
      handleClear();
    }
    setMessage(emptyMessage);
  };

  const handleKeydown = (e: React.KeyboardEvent<Element>, index: number) => {
    if (e.key === Keys.ENTER && headerData) {
      e.preventDefault();

      const filteredInput = headerData.GetProjectsHeaders.filter(
        (header) => header.initData
      );
      if (index === filteredInput.length - 1) {
        const input = document.getElementsByClassName("storybook-button--save");
        if (input.length) {
          (input[0] as HTMLInputElement).focus();
        }
      } else {
        const filteredInput = headerData.GetProjectsHeaders.filter(
          (header) => header.initData
        );
        const input = document.getElementsByName(
          `${filteredInput[index + 1].idx}`
        );

        if (input.length) {
          (input[0] as HTMLInputElement).focus();
        }
      }
    }
  };

  return (
    <div className="wp-project-details__initialize--add">
      <Title>
        {operation === Operation.CREATE ? "Fill" : "Update"} Initialize Data
      </Title>
      <img
        src={Close}
        alt=""
        onClick={() => setModal(false)}
        className="modal-close-icon"
      />
      {projectData && projectData.node && (
        <div className="wp-project-details__initialize--header">
          <b>{projectData.node.name}</b>
          <span>{projectData.node.supplierName.desc}</span>
        </div>
      )}

      {headerData
        ? headerData.GetProjectsHeaders.filter((header) => header.initData).map(
            (header, index) => {
              if (
                header.dataType === DataTypes.TEXT ||
                header.dataType === DataTypes.ALPHANUMERIC
              ) {
                return (
                  <>
                    <div key={header.id} className="label-grid">
                      <Label>{header.header}</Label>
                      <Input
                        name={`${header.idx}`}
                        onChange={handleValueChange}
                        autoFocus={index === 0}
                        value={formData[`${header.idx}`]}
                        onKeyDown={(e) => {
                          handleKeydown(e, index);
                        }}
                      />
                    </div>
                  </>
                );
              }
              if (header.dataType === DataTypes.DATE) {
                return (
                  <div key={header.id} className="label-grid">
                    <Label>{header.header}</Label>
                    <Input
                      type="date"
                      name={`${header.idx}`}
                      onChange={handleValueChange}
                      autoFocus={index === 0}
                      value={formData[`${header.idx}`]}
                      onKeyDown={(e) => {
                        handleKeydown(e, index);
                      }}
                    />
                  </div>
                );
              }
              if (
                header.dataType === DataTypes.FLOAT ||
                header.dataType === DataTypes.NUMBER
              ) {
                return (
                  <div key={header.id} className="label-grid">
                    <Label>{header.header}</Label>
                    <InputNumber
                      name={`${header.idx}`}
                      onChange={handleValueChange}
                      value={formData[`${header.idx}`]}
                      autoFocus={index === 0}
                      onKeyDown={(e: React.KeyboardEvent<Element>) => {
                        handleKeydown(e, index);
                      }}
                    />
                  </div>
                );
              }
              if (header.dataType === DataTypes.BOOLEAN) {
                return (
                  <div className="wp-project-details__initialize--form-labels">
               
                    <FormGroup key={header.id} >
                      <FormControlLabel
                        label={header.header}
                        labelPlacement="start"
                        control={
                          <AntSwitch
                            checked={formData[`${header.idx}`] as boolean}
                            autoFocus={index === 0}
                            onChange={(e) => {
                              setFormData({
                                ...formData,
                                [`${header.idx}`]: e.target.checked,
                              });
                            }}
                            name={`${header.idx}`}
                            onKeyDown={(e: React.KeyboardEvent<Element>) => {
                              handleKeydown(e, index);
                            }}
                          />
                        }
                      />
                    </FormGroup>
                  </div>
                );
              }
              return <></>;
            }
          )
        : null}
      <Button mode="save" loading={loading} onClick={handleSubmit} />
      <Button mode="clear" loading={loading} onClick={handleClear} />
      <Button mode="cancel" loading={loading} onClick={() => setModal(false)} />
      <MessageModal
        handleClose={handleClose}
        modalFlag={message.flag}
        operation={message.operation}
        value={message.message}
      />
    </div>
  );
};

export default InitializeData;
